/**
 * ContentHeader - 内容区域头部组件
 * 用于展示标题、标签状态、操作按钮、排序和搜索
 */

import React, { useState } from 'react';
import { Button, Tooltip, Tag, Space, Dropdown, Input } from 'antd';
import {
  CloudUploadOutlined,
  StarOutlined,
  StarFilled,
  EyeOutlined,
  EyeInvisibleOutlined,
  HeartOutlined,
  EditOutlined,
  SortAscendingOutlined,
  UserOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { ChevronRight } from 'lucide-react';
import type { TagGroup } from '../types';
import type { SortBy } from '../utils/postList';

interface ContentHeaderProps {
  // 简单标题模式：只需要 title，不需要 tagGroup
  title?: string;
  // 父级名称：如果提供，将显示为 "parentName > title" 格式
  parentName?: string;
  tagGroup?: TagGroup;
  isDark?: boolean;
  isTag?: boolean; // 是否是独立 Tag（而非 Tag Group）
  isShared?: boolean;
  isSubscribed?: boolean;
  isUploading?: boolean;
  isBatchUpdating?: boolean; // 批量更新中
  onShare?: () => void;
  onUpdateShare?: () => void;
  onSubscribe?: () => void;
  onUnsubscribe?: () => void;
  onToggleStar?: () => void;
  onToggleTracked?: () => void;
  onEdit?: () => void;
  onBatchUpdate?: () => void; // 批量更新博主回调
  showShareButtons?: boolean;
  showSubscribeButtons?: boolean;
  showStarButton?: boolean;
  showTrackedButton?: boolean;
  showEditButton?: boolean;
  showBatchUpdateButton?: boolean; // 是否显示批量更新按钮
  sortBy?: SortBy;
  onSortChange?: (sortBy: SortBy) => void;
  showSortDropdown?: boolean;
  // 搜索框
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export const ContentHeader: React.FC<ContentHeaderProps> = ({
  title,
  parentName,
  tagGroup,
  // isDark is kept for API compatibility but not used
  isDark: _isDark = false,
  isTag = false,
  isShared = false,
  isSubscribed = false,
  isUploading = false,
  isBatchUpdating = false,
  onShare,
  onUpdateShare,
  onSubscribe,
  onUnsubscribe,
  onToggleStar,
  onToggleTracked,
  onEdit,
  onBatchUpdate,
  showShareButtons = false,
  showSubscribeButtons = false,
  showStarButton = false,
  showTrackedButton = false,
  showEditButton = false,
  showBatchUpdateButton = false,
  sortBy = 'blogger',
  onSortChange,
  showSortDropdown = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = '搜索',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // 支持简单标题模式（不需要 tagGroup）
  const baseTitle = title || tagGroup?.name || '';
  // 如果有 parentName，显示为 "parentName > title" 格式
  const displayTitle = parentName ? (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {parentName}
      <ChevronRight size={16} style={{ margin: '2px 2px 0px 2px', color: 'var(--ant-color-text-tertiary)' }} />
      {baseTitle}
    </span>
  ) : baseTitle;
  const description = tagGroup ? ((tagGroup as any).shareDescription || (tagGroup as any).description) : undefined;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        padding: '0px 0px',
        minHeight: '56px',
        borderRadius: '8px',
      }}
    >
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: description ? '2px' : '0' }}>
          <h2 style={{ margin: 0 }}>{displayTitle}</h2>
        {tagGroup?.isStarred && (
          <Tag color="gold" style={{ margin: 0 }}>
            <StarFilled style={{ marginRight: 4 }} />
            已标星
          </Tag>
        )}
        {tagGroup?.isTracked && (
          <Tag color="blue" style={{ margin: 0 }}>
            <EyeOutlined style={{ marginRight: 4 }} />
            已追踪
          </Tag>
        )}
        {/* {isShared && (
          <Tag style={{ margin: 0, border: 'none', background: 'var(--ant-color-fill-secondary)', color: 'var(--ant-color-text-secondary)' }}>
            <CloudUploadOutlined style={{ marginRight: 4 }} />
            已分享
          </Tag>
        )} */}
        {isSubscribed && (
          <Tag style={{ margin: 0, border: 'none', background: 'var(--ant-color-fill-secondary)', color: 'var(--ant-color-text-secondary)' }}>
            已订阅
          </Tag>
        )}

        {/* 编辑按钮 - hover 时显示 */}
        {showEditButton && onEdit && (
          <EditOutlined
            onClick={onEdit}
            style={{
              fontSize: '14px',
              color: 'var(--ant-color-text-tertiary)',
              cursor: 'pointer',
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--ant-color-text)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--ant-color-text-tertiary)';
            }}
          />
        )}
      </div>

      {/* 描述 */}
      {description && (
        <div
          style={{
            fontSize: '13px',
            lineHeight: '1.5',
            color: 'var(--ant-color-text-secondary)',
          }}
        >
          {description}
        </div>
      )}
      </div>

      <Space>
        {/* 搜索框 */}
        {onSearchChange && (
          <Input
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-tertiary)' }} />}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            allowClear
            style={{ width: 200 }}
            variant="filled"
          />
        )}

        {/* 批量更新博主按钮 - 仅对 Tag 显示 */}
        {showBatchUpdateButton && isTag && onBatchUpdate && (
          <Tooltip title="更新该分类下所有博主的帖子数据">
            <Button
              icon={<SyncOutlined spin={isBatchUpdating} />}
              onClick={onBatchUpdate}
              loading={isBatchUpdating}
              disabled={isBatchUpdating}
            >
              {isBatchUpdating ? '更新中...' : '批量更新博主'}
            </Button>
          </Tooltip>
        )}

        {/* 排序 Dropdown */}
        {showSortDropdown && onSortChange && (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'blogger',
                  icon: <UserOutlined />,
                  label: '按博主粉丝量排序',
                  onClick: () => onSortChange('blogger'),
                },
                {
                  key: 'time',
                  icon: <ClockCircleOutlined />,
                  label: '按剪藏时间排序',
                  onClick: () => onSortChange('time'),
                },
              ],
              selectedKeys: [sortBy],
            }}
            trigger={['click']}
          >
            <Button icon={<SortAscendingOutlined />}>
              {sortBy === 'blogger' ? '按博主粉丝量排序' : '按剪藏时间排序'}
            </Button>
          </Dropdown>
        )}

        {showStarButton && onToggleStar && (
          <Tooltip title={tagGroup?.isStarred ? '取消标星' : '标星'}>
            <Button
              type={tagGroup?.isStarred ? 'primary' : 'default'}
              icon={tagGroup?.isStarred ? <StarFilled /> : <StarOutlined />}
              onClick={onToggleStar}
            />
          </Tooltip>
        )}

        {showTrackedButton && onToggleTracked && (
          <Tooltip title={tagGroup?.isTracked ? '取消追踪' : '追踪'}>
            <Button
              type={tagGroup?.isTracked ? 'primary' : 'default'}
              icon={tagGroup?.isTracked ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={onToggleTracked}
            />
          </Tooltip>
        )}

        {showShareButtons && !isShared && onShare && (
          <Tooltip title="分享到广场">
            <Button
              type="primary"
              icon={isUploading ? undefined : <CloudUploadOutlined />}
              onClick={onShare}
              loading={isUploading}
              disabled={isUploading}
            >
              {isUploading ? '上传中...' : '分享到广场'}
            </Button>
          </Tooltip>
        )}

        {showShareButtons && isShared && (
          <>
            {onUpdateShare && (
              <Tooltip title="更新分享信息">
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={onUpdateShare}
                  loading={isUploading}
                  disabled={isUploading}
                >
                  {isUploading ? '更新中...' : '更新分享信息'}
                </Button>
              </Tooltip>
            )}
          </>
        )}

        {showSubscribeButtons && !isSubscribed && onSubscribe && (
          <Tooltip title="订阅此 Tag Group">
            <Button
              type="primary"
              icon={<HeartOutlined />}
              onClick={onSubscribe}
            >
              订阅
            </Button>
          </Tooltip>
        )}

        {showSubscribeButtons && isSubscribed && onUnsubscribe && (
          <Tooltip title="取消订阅">
            <Button
              danger
              onClick={onUnsubscribe}
            >
              取消订阅
            </Button>
          </Tooltip>
        )}
      </Space>
    </div>
  );
};

export default ContentHeader;
